import React from 'react';
import { CompleteKundali, ChartStyle } from '../../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from '../ChartViewer';
import { SudarshanChakraSvg } from '../SudarshanChakraSvg';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';
import { getPlanetFullName } from '../../utils/language';

interface Grantha2Props extends GranthaCommonProps {
  chartStyle?: ChartStyle;
}

export const Grantha2_Shodashvarga: React.FC<Grantha2Props> = ({
  kundali,
  lang,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter,
  chartStyle = 'north'
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const charts = (kundali.charts || {}) as Record<string, any>;
  const { birthDetails, planets = [] } = kundali;

  const renderChart = (chartData: any, customPlanets?: any[], size: string = 'max-w-[210px]') => {
    if (!chartData) return null;
    const chartPlanets = customPlanets || chartData.planets || planets || [];
    if (chartStyle === 'south') {
      return (
        <div className={`w-full aspect-square ${size} mx-auto`}>
          <SouthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={lang} />
        </div>
      );
    }
    if (chartStyle === 'east') {
      return (
        <div className={`w-full aspect-square ${size} mx-auto`}>
          <EastIndianSvgChart chartData={chartData} planets={chartPlanets} lang={lang} />
        </div>
      );
    }
    return (
      <div className={`w-full aspect-square ${size} mx-auto`}>
        <NorthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={lang} />
      </div>
    );
  };

  const vargaBhedaList = [
    { name: isHi ? 'पारिजात (Parijata)' : 'Parijata', count: '2 Vargas', effect: isHi ? 'मान-सम्मान, यश व कीर्ति' : 'Respect, esteem & fame' },
    { name: isHi ? 'उत्तम (Uttama)' : 'Uttama', count: '3 Vargas', effect: isHi ? 'उत्तम बुद्धि व नैतिक आचरण' : 'High intellect & virtue' },
    { name: isHi ? 'गोपुर (Gopura)' : 'Gopura', count: '4 Vargas', effect: isHi ? 'प्रचुर धन, वाहन व भूमि सुख' : 'Abundant assets & estate' },
    { name: isHi ? 'सिंहासन (Simhasana)' : 'Simhasana', count: '5 Vargas', effect: isHi ? 'राजकीय पद, अधिकार व प्रभुत्व' : 'Royal seat, power & governance' },
    { name: isHi ? 'पारावत (Paravata)' : 'Paravata', count: '6 Vargas', effect: isHi ? 'गज-वाहन, छत्र व विशाल साम्राज्य' : 'Supreme luxury, royal conveyances' },
    { name: isHi ? 'देवलोक (Devaloka)' : 'Devaloka', count: '7 Vargas', effect: isHi ? 'देव तुल्य प्रतिष्ठा व तपस्या' : 'Divine reverence & aura' },
    { name: isHi ? 'ऐरावत (Airavata)' : 'Airavata', count: '8+ Vargas', effect: isHi ? 'चक्रवर्ती सम्राट सदृश अमर यश' : 'Sovereign emperor-grade stature' }
  ];

  const higherTriads = [
    {
      page: 10,
      pageHi: '१०',
      title: isHi ? 'होरा (D2), द्रेष्काण (D3) एवं चतुर्थांश (D4)' : 'Hora (D2), Drekkana (D3) & Chaturthamsha (D4)',
      sub: isHi ? 'धन संपदा, पराक्रम/अनुज तथा अचल संपत्ति व गृह सुख।' : 'Wealth distribution, sibling courage, fixed estate & real assets.',
      c1: { title: isHi ? 'D2 होरा (धन)' : 'D2 Hora (Wealth)', d: charts.D2 || charts.D1 },
      c2: { title: isHi ? 'D3 द्रेष्काण (पराक्रम)' : 'D3 Drekkana (Courage)', d: charts.D3 || charts.D1 },
      c3: { title: isHi ? 'D4 चतुर्थांश (संपत्ति)' : 'D4 Chaturthamsha (Assets)', d: charts.D4 || charts.D1 }
    },
    {
      page: 11,
      pageHi: '११',
      title: isHi ? 'सप्तांश (D7), दशमांश (D10) एवं द्वादशांश (D12)' : 'Saptamsha (D7), Dashamsha (D10) & Dwadashamsha (D12)',
      sub: isHi ? 'संतान/वंश, कर्म/आजीविका का सर्वोच्च शिखर तथा माता-पिता/पितृ ऋण।' : 'Lineage progeny, career prestige, executive achievements and ancestral heritage.',
      c1: { title: isHi ? 'D7 सप्तांश (संतान)' : 'D7 Saptamsha (Progeny)', d: charts.D7 || charts.D1 },
      c2: { title: isHi ? 'D10 दशमांश (कर्म)' : 'D10 Dashamsha (Career)', d: charts.D10 || charts.D1 },
      c3: { title: isHi ? 'D12 द्वादशांश (माता-पिता)' : 'D12 Dwadashamsha (Parents)', d: charts.D12 || charts.D1 }
    },
    {
      page: 12,
      pageHi: '१२',
      title: isHi ? 'षोडशांश (D16), विंशांश (D20) एवं चतुर्विंशांश (D24)' : 'Shodashamsha (D16), Vimshamsha (D20) & Chaturvimshamsha (D24)',
      sub: isHi ? 'वाहन/सुख-साधन, आध्यात्मिक उपासना एवं उच्च विद्या/ज्ञान।' : 'Vehicular luxury, esoteric spiritual devotion and scholarly knowledge.',
      c1: { title: isHi ? 'D16 षोडशांश (सुख)' : 'D16 Shodashamsha (Vehicles)', d: charts.D16 || charts.D1 },
      c2: { title: isHi ? 'D20 विंशांश (साधना)' : 'D20 Vimshamsha (Spirituality)', d: charts.D20 || charts.D1 },
      c3: { title: isHi ? 'D24 चतुर्विंशांश (विद्या)' : 'D24 Chaturvimshamsha (Learning)', d: charts.D24 || charts.D1 }
    },
    {
      page: 13,
      pageHi: '१३',
      title: isHi ? 'सप्तविंशांश (D27), त्रिंशांश (D30) एवं खवेदांश (D40)' : 'Saptavimshamsha (D27), Trimshamsha (D30) & Khavedamsha (D40)',
      sub: isHi ? 'शारीरिक बल/सामर्थ्य, अरिष्ट/रोग संघर्ष तथा शुभ-अशुभ कर्म परिपाक।' : 'Inherent stamina, structural vulnerabilities and auspicious karma fruition.',
      c1: { title: isHi ? 'D27 सप्तविंशांश (बल)' : 'D27 Saptavimshamsha (Stamina)', d: charts.D27 || charts.D1 },
      c2: { title: isHi ? 'D30 त्रिंशांश (अरिष्ट)' : 'D30 Trimshamsha (Arishta)', d: charts.D30 || charts.D1 },
      c3: { title: isHi ? 'D40 खवेदांश (शुभता)' : 'D40 Khavedamsha (Auspices)', d: charts.D40 || charts.D1 }
    }
  ];

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 7: FOUNDATIONAL TRINITY MANDALAS (LAGNA D1, CHANDRA & NAVAMSHA D9)   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ २ • प्रधान कुण्डली त्रयी' : 'Grantha 2 • Foundational Trinity'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ७' : 'Page 7'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रधान कुण्डली त्रयी (लग्न D1, चन्द्र कुण्डली एवं नवांश D9)' : 'FOUNDATIONAL TRINITY MANDALAS (D1, CHANDRA, D9)'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'लग्न कुण्डली (स्थूल शरीर व भौतिक जीवन), चन्द्र कुण्डली (सूक्ष्म मन) तथा नवांश (कारण आत्मा व कलत्र भाग्य)।'
                : 'Lagna D1 (Manifest body & personality), Chandra Chart (Mind & subconscious), and Navamsha D9 (Destiny, soul & spouse).'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{isHi ? 'लग्न कुण्डली (D1)' : 'Lagna Chart (D1)'}</h3>
              {renderChart(charts.D1 || charts.lagna, undefined, 'max-w-[210px]')}
            </div>
            <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{isHi ? 'चन्द्र कुण्डली (Chandra)' : 'Chandra Chart (Mind)'}</h3>
              {renderChart(charts.chandra || charts.D1, undefined, 'max-w-[210px]')}
            </div>
            <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{isHi ? 'नवांश कुण्डली (D9)' : 'Navamsha Chart (D9)'}</h3>
              {renderChart(charts.D9 || charts.navamsha, undefined, 'max-w-[210px]')}
            </div>
          </div>

          <div className={`rounded-xl border p-2.5 text-xs ${tableRowEven}`}>
            <span className="font-bold text-astro-primary">{isHi ? 'शास्त्रीय समन्वय नियम: ' : 'Classical Synthesis Rule: '}</span>
            <span className="text-gray-700">
              {isHi
                ? 'लग्न कुण्डली जो वचन देती है, नवांश कुण्डली उसकी पुष्टि करती है। चन्द्र कुण्डली जातक के मानसिक संकल्प और ग्रहणशीलता का निर्धारण करती है।'
                : 'What is promised in Lagna D1 is verified and fulfilled in Navamsha D9, with Chandra detailing psychological receptivity.'}
            </span>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ २ • पृष्ठ ७' : 'Grantha 2 • Page 7')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 8: SURYA KUNDALI & KARAKAMSHA (JAIMINI)                              */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ २ • सूर्य एवं कारकांश कुण्डली' : 'Grantha 2 • Surya & Karakamsha'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ८' : 'Page 8'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'सूर्य कुण्डली एवं जैमिनी कारकांश कुण्डली' : 'SURYA KUNDALI & JAIMINI KARAKAMSHA'}
            </h2>
            <p className={`text-xs ${mutedText}`}>
              {isHi
                ? 'आत्मा के कारक सूर्य की स्थिति तथा जैमिनी पद्धति से आत्मा की अंतिम मुमुक्षु स्थिति (कारकांश)।'
                : 'Solar Lagna (Surya Kundali) reflecting soul vitality and Jaimini Atmakaraka (Karakamsha) revealing spiritual destiny.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className={`rounded-2xl border p-4 text-center space-y-2 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-sm ${headingPrimary}`}>{isHi ? 'सूर्य कुण्डली (Surya Kundali)' : 'Surya Kundali (Sun Lagna)'}</h3>
              {renderChart(charts.surya || charts.D1, undefined, 'max-w-[260px]')}
              <p className={`text-xs ${mutedText}`}>{isHi ? 'आत्मबल, अधिकार एवं शासकीय प्रतिष्ठा' : 'Solar vitality, authority & public standing'}</p>
            </div>
            <div className={`rounded-2xl border p-4 text-center space-y-2 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-sm ${headingPrimary}`}>{isHi ? 'कारकांश कुण्डली (Karakamsha)' : 'Karakamsha Kundali (Jaimini)'}</h3>
              {renderChart(charts.karakamsha || charts.D9 || charts.D1, undefined, 'max-w-[260px]')}
              <p className={`text-xs ${mutedText}`}>{isHi ? 'जैमिनी आत्मांश, सहज स्वभाव एवं मोक्ष मार्ग' : 'Jaimini Atmakaraka inner soul blueprint'}</p>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ २ • पृष्ठ ८' : 'Grantha 2 • Page 8')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 9: DEDICATED FULL-PAGE SUDARSHAN CHAKRA MANDALA                     */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ २ • त्रि-वलय सुदर्शन चक्र महामण्डल' : 'Grantha 2 • 3-Ring Sudarshan Chakra'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ९' : 'Page 9'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रामाणिक त्रि-वलय सुदर्शन चक्र महामण्डल' : 'AUTHENTIC 3-RING SUDARSHAN CHAKRA MANDALA'}
            </h2>
            <p className={`text-xs ${mutedText}`}>
              {isHi
                ? 'बाह्य वृत्त (सूर्य लग्न / आत्मा), मध्य वृत्त (चन्द्र लग्न / मन) एवं आंतरिक वृत्त (जन्म लग्न / देह) का समकालिक संगम।'
                : '3-Concentric Ring Mandala: Outer (Surya Lagna), Middle (Chandra Lagna), and Inner (Janma Lagna) synchronized simultaneously.'}
            </p>
          </div>

          {/* Big Sudarshan Chakra occupying entire page */}
          <div className="w-full flex justify-center py-2">
            <div className="w-full max-w-[420px] aspect-square">
              <SudarshanChakraSvg kundali={kundali} lang={lang} theme="deluxe" />
            </div>
          </div>

          {/* Planetary Longitude Coordinates Table */}
          <div className={`rounded-xl p-3 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1 mb-2 text-xs uppercase tracking-wider ${headingPrimary}`}>
              {isHi ? 'त्रिवलय ग्रह स्थिति सारिणी (Tri-Fold Planetary Coordinates)' : 'Tri-Fold Planetary Coordinates'}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px] text-center">
              {planets.filter(p => p.name !== 'Ascendant').slice(0, 6).map((p, pIdx) => (
                <div key={pIdx} className={`p-1.5 rounded border ${tableBorder} ${tableRowEven}`}>
                  <span className="block font-bold text-astro-primary">{getPlanetFullName(p.name, lang)}</span>
                  <span className="font-mono text-[10px] block text-gray-700">{p.sign}</span>
                  <span className="font-mono font-bold text-[10px] text-astro-accent">{p.degreeFormatted}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ २ • पृष्ठ ९' : 'Grantha 2 • Page 9')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 9: HIGHER MANDALAS: SARVATOBHADRA & KOTA / KAALCHAKRA               */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ २ • सर्वतोभद्र एवं कालचक्र मण्डल' : 'Grantha 2 • Sarvatobhadra & Kota Chakra'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ९' : 'Page 9'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'सर्वतोभद्र चक्र एवं कोट/कालचक्र महामण्डल (वेध विचार)' : 'SARVATOBHADRA CHAKRA & KOTA / KAALCHAKRA GEOMETRY'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? '२८ नक्षत्रों, स्वर, वर्ण, राशि एवं तिथि वेध का प्रामाणिक चक्र तथा दुर्ग रक्षा (कोट चक्र)।'
                : '28-Nakshatra Sarvatobhadra cross-vedha matrix along with Kota Chakra bastion defense dynamics.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Box A: Sarvatobhadra Chakra Graphic / Grid */}
            <div className={`rounded-xl border p-3 space-y-2 ${innerCardBg}`}>
              <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
                {isHi ? 'सर्वतोभद्र चक्र (Sarvatobhadra Matrix)' : 'Sarvatobhadra 9×9 Vedha Grid'}
              </h3>
              <div className="grid grid-cols-5 gap-1 text-[10px] text-center font-mono">
                {['अ', 'कृ', 'रो', 'मृ', 'इ', 'आ', 'पु', 'पुष्य', 'अश्ले', 'उ', 'ऋ', 'मघा', 'पू.फा', 'उ.फा', 'ए', 'लृ', 'हस्त', 'चित्रा', 'स्वा', 'ऐ', 'ओ', 'विशा', 'अनु', 'ज्ये', 'औ'].map((item, idx) => (
                  <div key={idx} className="p-1 rounded border bg-white/70 font-semibold truncate">
                    {item}
                  </div>
                ))}
              </div>
              <p className={`text-[10px] leading-relaxed ${mutedText}`}>
                {isHi
                  ? 'अग्र वेध, दक्षिण वेध, वाम वेध एवं कोण वेध द्वारा ग्रहों की संक्रांति का सूक्ष्म परीक्षण।'
                  : 'Front, right, left and diagonal vedha vectors determining planetary impact on natal sensitivity points.'}
              </p>
            </div>

            {/* Box B: Kota Chakra Bastion Analysis */}
            <div className={`rounded-xl border p-3 space-y-2 ${innerCardBg}`}>
              <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
                {isHi ? 'कोट चक्र दुर्ग रक्षा विश्लेषण (Kota Bastion)' : 'Kota Chakra Bastion Zones'}
              </h3>
              <div className="space-y-1.5 text-xs">
                <div className="p-1.5 rounded border bg-emerald-50/80 border-emerald-200">
                  <span className="font-bold text-emerald-900">Stambha (Inner Pillar):</span>
                  <span className="text-gray-700 ml-1">Lagna nakshatra & vital stability core.</span>
                </div>
                <div className="p-1.5 rounded border bg-blue-50/80 border-blue-200">
                  <span className="font-bold text-blue-900">Madhya Durg (Middle Fortress):</span>
                  <span className="text-gray-700 ml-1">Karmic allies & secondary fortification.</span>
                </div>
                <div className="p-1.5 rounded border bg-amber-50/80 border-amber-200">
                  <span className="font-bold text-amber-900">Prakaara (Ramparts):</span>
                  <span className="text-gray-700 ml-1">Perimeter defensive walls against afflictions.</span>
                </div>
                <div className="p-1.5 rounded border bg-red-50/80 border-red-200">
                  <span className="font-bold text-red-900">Bahya (Outer Moat):</span>
                  <span className="text-gray-700 ml-1">External transit influences & hostile entries.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ २ • पृष्ठ ९' : 'Grantha 2 • Page 9')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 10 TO 13: 4 DENSE PAGES COVERING 12 HIGHER DIVISIONAL CHARTS        */}
      {/* ========================================================================= */}
      {higherTriads.map((triad, idx) => (
        <div key={idx} className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
          <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{isHi ? `ग्रन्थ २ • षोडशवर्ग चक्र (पृष्ठ ${triad.pageHi})` : `Grantha 2 • Shodashvarga (Page ${triad.page})`}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${triad.pageHi}` : `Page ${triad.page}`}</span>
          </div>

          <div className="space-y-3 my-auto">
            <div className="text-center space-y-0.5 border-b pb-1.5">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {triad.title}
              </h2>
              <p className={`text-[11px] ${mutedText}`}>
                {triad.sub}
              </p>
            </div>

            {/* 3 Divisional Charts packed horizontally */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
                <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{triad.c1.title}</h3>
                {renderChart(triad.c1.d, undefined, 'max-w-[210px]')}
              </div>
              <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
                <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{triad.c2.title}</h3>
                {renderChart(triad.c2.d, undefined, 'max-w-[210px]')}
              </div>
              <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
                <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>{triad.c3.title}</h3>
                {renderChart(triad.c3.d, undefined, 'max-w-[210px]')}
              </div>
            </div>
          </div>

          {renderPageFooter(isHi ? `ग्रन्थ २ • पृष्ठ ${triad.pageHi}` : `Grantha 2 • Page ${triad.page}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 14: D45, D60 & VARGA DIGNITY MATRIX (VIMSOPAKA & VARGA BHEDAS)        */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ २ • D45, D60 एवं वर्ग भेद सारिणी' : 'Grantha 2 • D45, D60 & Varga Dignity'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १४' : 'Page 14'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'अक्षवेदांश (D45), षष्ट्यंश (D60) एवं षोडशवर्ग भेद सारिणी' : 'AKSHAVEDAMSHA (D45), SHASHTYAMSHA (D60) & VARGA BHEDA MATRIX'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'चरित्र व आचरण (D45), पूर्व जन्म संचित कर्म बीज (D60) तथा विंशोपक बल वर्ग भेद (पारिजात से ऐरावत)।'
                : 'Virtue & integrity (D45), past-life karmic seeds (D60) and Parashari Varga Bheda counts.'}
            </p>
          </div>

          {/* D45 and D60 side-by-side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>
                {isHi ? 'D45 अक्षवेदांश (चरित्र व आचरण)' : 'D45 Akshavedamsha (Virtue & Integrity)'}
              </h3>
              {renderChart(charts.D45 || charts.D1, undefined, 'max-w-[210px]')}
            </div>
            <div className={`rounded-xl border p-2.5 text-center space-y-1 ${innerCardBg}`}>
              <h3 className={`font-bold font-serif text-xs ${headingPrimary}`}>
                {isHi ? 'D60 षष्ट्यंश (संचित कर्म बीज)' : 'D60 Shashtyamsha (Past Karma Root)'}
              </h3>
              {renderChart(charts.D60 || charts.D1, undefined, 'max-w-[210px]')}
            </div>
          </div>

          {/* Varga Bhedas Table */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
              {isHi ? 'पाराशरी षोडशवर्ग भेद वर्गीकरण (Varga Bhedas)' : 'Parashari Varga Bheda Dignity Hierarchy (2 to 8+ Vargas)'}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 text-xs">
              {vargaBhedaList.map((vb, idx) => (
                <div key={idx} className="p-1.5 rounded border bg-white/70">
                  <div className="font-bold text-astro-primary">{vb.name}</div>
                  <div className="text-[11px] font-mono font-semibold text-gray-800">{vb.count}</div>
                  <div className={`text-[10px] ${mutedText}`}>{vb.effect}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Native Attained Varga Bhedas */}
          {kundali.vargaBhedas && kundali.vargaBhedas.length > 0 && (
            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <div className={`p-1.5 font-bold text-xs uppercase border-b ${tableHeaderBg} ${headingPrimary}`}>
                {isHi ? 'जातक षोडशवर्ग भेद स्थिति (Native Attained Varga Bhedas)' : 'Native Attained Shodashvarga Dignities (16 Vargas)'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-1.5 p-2 text-xs">
                {kundali.vargaBhedas.map((vb) => {
                  const hasBheda = vb.vargaBhedaTitle !== 'Ordinary';
                  return (
                    <div key={vb.planet} className={`p-1.5 rounded border text-center ${hasBheda ? 'bg-amber-50/80 border-amber-300' : 'bg-white/60'}`}>
                      <div className="font-bold text-astro-primary">{getPlanetFullName(vb.planet, lang)}</div>
                      <div className="text-[11px] font-mono font-bold text-gray-800">{vb.auspiciousVargaCount}/16 वर्ग</div>
                      <div className={`text-[10px] font-semibold ${hasBheda ? 'text-amber-800 font-bold' : 'text-gray-400'}`}>
                        {isHi ? vb.vargaBhedaTitleHi : vb.vargaBhedaTitle}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ २ • पृष्ठ १४' : 'Grantha 2 • Page 14')}
      </div>
    </>
  );
};
